import React, { useState } from "react";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Alert,
} from "@mui/material";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import { useSelector } from "react-redux";
import { getAccessToken } from "../../../utils/tokenManager";
import { getAuthToken } from "../../../utils/getAuthToken";
import { disconnectShopify } from "../../../services/shopifyApi";

/**
 * Shared "Disconnect Shopify" action — used both on the Integrations card
 * directly and inside ShopifyDetailsDialog, so both entry points get the
 * same confirmation step. Disconnecting hard-deletes the integration record
 * server-side (see disconnectShopify in shopifyOAuthService.js) — there's no
 * undo, hence the confirmation.
 */
const DisconnectShopifyButton = ({ onDisconnected, variant = "outlined", size = "small", sx }) => {
  const workspaceId = useSelector(
    (s) => s.workspace?.currentWorkspace?._id || s.auth?.user?.workspaceId
  );

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const handleConfirmDisconnect = async () => {
    if (!workspaceId) return;
    setDisconnecting(true);
    setErrorMessage("");
    try {
      let token = getAccessToken();
      if (!token) token = await getAuthToken();
      await disconnectShopify(workspaceId, token);
      setConfirmOpen(false);
      onDisconnected?.();
    } catch (err) {
      setErrorMessage(err.response?.data?.message || err.message || "Failed to disconnect Shopify store.");
    } finally {
      setDisconnecting(false);
    }
  };

  return (
    <>
      <Button
        variant={variant}
        color="error"
        size={size}
        startIcon={<LinkOffIcon />}
        onClick={() => setConfirmOpen(true)}
        sx={{ textTransform: "none", width: "fit-content", ...sx }}
      >
        Disconnect
      </Button>

      <Dialog open={confirmOpen} onClose={() => (disconnecting ? null : setConfirmOpen(false))} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontSize: "1.4rem", fontWeight: 700, pt: 3, px: 3 }}>
          Disconnect Shopify Store
        </DialogTitle>
        <DialogContent sx={{ px: 3 }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to disconnect your Shopify store? Order triggers, workflow automations, and
            order history for this workspace will stop working immediately.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This action cannot be undone. You'll need to reconnect from scratch to restore this integration.
          </Typography>
          {errorMessage && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {errorMessage}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button
            onClick={() => setConfirmOpen(false)}
            disabled={disconnecting}
            size="large"
            sx={{ textTransform: "none", fontSize: "1rem" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDisconnect}
            disabled={disconnecting}
            size="large"
            sx={{ textTransform: "none", fontSize: "1rem", px: 3 }}
          >
            {disconnecting ? "Disconnecting..." : "Yes, Disconnect"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DisconnectShopifyButton;
